import { defaultScheduleEndDate, defaultScheduleStartDate, parseScheduleDate } from "@repo/shared";
import {
  type AccountMinimumViolation,
  byFrequency,
  countDeposits,
  type DynamicGenerationMeta,
  type GeneratedPositionItem,
  type MinBuyViolation,
  type MissingRequirementReason,
  type PortfolioFollowupSource,
  type PortfolioType,
  perDepositFromTotal,
  RecurringFrequency,
  type ScheduleType,
  type SnapshotInvalidation,
  totalFromPerDeposit,
} from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertMessage } from "@/components/ui/alert-message";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MonthYearPicker } from "@/components/ui/month-year-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import * as portfoliosApi from "@/lib/api/flow-instances/portfolios";
import * as publicPortfoliosApi from "@/lib/api/public-flow/portfolios";
import { usePublicSession } from "@/lib/context/public-session-context";
import { useInvalidateFlowCompletion } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { goalQueryKeys } from "@/lib/hooks/use-flow-instances/use-goals";
import {
  portfolioSnapshotQueryKeys,
  useInvalidatePortfolioSnapshots,
} from "@/lib/hooks/use-flow-instances/use-portfolio-snapshots";
import {
  invalidateGoalPortfolios,
  invalidatePortfolioFamily,
  portfolioQueryKeys,
} from "@/lib/hooks/use-flow-instances/use-portfolios";
import { useGoalPlatformState } from "@/lib/hooks/use-goal-platforms";
import {
  publicPortfolioSnapshotQueryKeys,
  useInvalidatePublicPortfolioSnapshots,
} from "@/lib/hooks/use-public-flow/use-goal-snapshots";
import { publicGoalQueryKeys } from "@/lib/hooks/use-public-flow/use-goals";
import {
  invalidatePublicGoalPortfolios,
  invalidatePublicPortfolioFamily,
  publicPortfolioKeys,
} from "@/lib/hooks/use-public-flow/use-portfolios";
import { publicFlowCompletionQueryKey } from "@/lib/hooks/use-public-flow/use-public-flow-completion";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { ComponentStyleType } from "@/lib/types/flow";
import { formatMonthYear } from "@/lib/utils";
import { formatAccountMinimumLines } from "./account-minimum-line";
import { DynamicCompositionView } from "./dynamic-composition-view";
import { formatFollowupSourceLabel } from "./followup-source-label";
import { ModelPortfolioSelect } from "./model-portfolio-select";
import { T } from "./portfolio-input-widget.i18n";

// Stored values are zone-less UTC ("YYYY-MM-DD HH:MM:SS"): parse them as UTC, never
// host-local, or a zone east of UTC reads (and re-saves) the previous month.
function parseInitialDate(date: string | null | undefined): string {
  return parseScheduleDate(date)?.toISOString().slice(0, 7) ?? "";
}

/** What a portfolio update returns that the widget reacts to. */
type SaveResult = { instanceId?: string; refreshHints?: SnapshotInvalidation };

// QP-150 — portfolio_input edits a portfolio's name, schedule/amounts/dates, and the
// model/custom toggle. Mirrors the former goal_input form, scoped to a portfolio.
type PortfolioInputWidgetProps = {
  /**
   * QL-664 — a mandate template governs the goal (server-derived): model and
   * dynamic render disabled with an annotation and the fallback picks custom.
   * A portfolio already of a restricted type keeps its value; the completion
   * marker says to switch it.
   */
  templateRestricted?: boolean;
  goalId: string;
  portfolioId: string;
  initialData?: {
    name: string;
    scheduleType: ScheduleType;
    oneTimeAmount?: number;
    oneTimeDate?: string | null;
    recurringAmount?: number;
    recurringFrequency?: RecurringFrequency;
    recurringStartDate?: string | null;
    recurringEndDate?: string | null;
    portfolioType: PortfolioType;
  };
  disabled?: boolean;
  /**
   * QL-309 — what a follow-up portfolio is linked to (from the portfolio read
   * response). Its `lockedScheduleType` pins the Schedule Type select — account
   * → one-time, savings agreement → recurring; updatePortfolio and flow
   * validation enforce it server-side, this is the UX mirror.
   */
  followupSource?: PortfolioFollowupSource | null;
  /**
   * QL-746 — the name field. Hidden, the portfolio keeps the name it was
   * created with ("Portfolio 1"): the unchanged value is saved back as is.
   */
  showPortfolioName?: boolean;
  showPortfolioType?: boolean;
  allowedPortfolioTypes?: PortfolioType[];
  /**
   * QP-185 — the collection's opt-in dynamic setting. When false and the config
   * still allows "dynamic", the option renders disabled with an annotation
   * (never hidden, never auto-replaced by a fallback type).
   */
  dynamicPortfoliosEnabled: boolean;
  /**
   * QL-72 — the collection allows a model portfolio from another risk score:
   * a model portfolio shows the model-portfolio picker.
   */
  modelDeviationEnabled?: boolean;
  /**
   * QL-72 — the hand-picked model portfolio (from the portfolio read; null =
   * Automatic). With the setting off the picker still shows for a pick, so it
   * can be returned to Automatic.
   */
  pickedPortfolioId?: string | null;
  showDateInputs?: boolean;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  // Callback after a successful save (receives the regenerated-snapshot refresh hints).
  onSaveComplete?: (refreshHints?: SnapshotInvalidation) => void | Promise<void>;
  hasValidationError?: boolean;
  /**
   * QL-198 — machine-readable completion-gate reason for this component
   * (`missing[].reason` via the completion markers). The widget renders a
   * specific banner for the model-resolution reasons
   * (`no_model_portfolio_for_score[_on_platform]`); other/absent values fall
   * back to the generic incomplete treatment.
   */
  missingReason?: MissingRequirementReason;
  /**
   * QL-198 — the goal's current risk score, interpolated into the
   * model-resolution banner ("…for risk score N…").
   */
  goalRiskScore?: number | null;
  /**
   * Server-computed min-buy breaches for this portfolio (QP-193) — empty unless the
   * collection enforces minimum buy amounts. For a model portfolio each entry carries
   * the portfolio amount needed to clear the product's minimum.
   */
  minBuyViolations?: MinBuyViolation[];
  /**
   * QL-362 — accounts this portfolio's one-time buys would leave below their
   * wrapper type's minimum. A goal-level aggregate: siblings feeding the same
   * account carry the same entry. Rendered for model portfolios only — on a
   * custom portfolio the custom-portfolio builder shows the same lines.
   */
  accountMinimumViolations?: AccountMinimumViolation[];
  /**
   * QP-185 — a dynamic portfolio's generated composition + generation outcome
   * (from the portfolio read response). Rendered read-only below the form.
   */
  generatedPositions?: GeneratedPositionItem[];
  dynamicGeneration?: DynamicGenerationMeta | null;
  /**
   * QP-185 — flow instance id (advisor surface only) so a lazily regenerated
   * composition can refresh the completion query. The public surface derives
   * the instance from the session and leaves this unset.
   */
  instanceId?: string | null;
};

/**
 * Last generatedAt reacted to, per portfolio id. Module-scoped on purpose: the
 * reaction must fire once per new generation even across widget remounts (a
 * cold page load mounts the widget with the freshly regenerated value already
 * in place — a mount-skipping ref would miss it). One entry per portfolio, so
 * it stays bounded no matter how often a portfolio regenerates.
 */
const seenDynamicGenerations = new Map<string, string>();

/**
 * Portfolio Input Widget (QP-150)
 * Inline form for editing a portfolio's name + schedule/amounts/type. Rendered once per
 * portfolio on the portfolio page. Shared by the advisor and public surfaces.
 */
export function PortfolioInputWidget({
  goalId,
  portfolioId,
  initialData,
  disabled = false,
  followupSource = null,
  showPortfolioName = true,
  showPortfolioType = true,
  allowedPortfolioTypes = ["model", "custom", "dynamic"],
  dynamicPortfoliosEnabled,
  modelDeviationEnabled = false,
  pickedPortfolioId = null,
  templateRestricted = false,
  showDateInputs = false,
  style = "border",
  title,
  description,
  onSaveComplete,
  hasValidationError = false,
  missingReason,
  goalRiskScore = null,
  minBuyViolations = [],
  accountMinimumViolations = [],
  generatedPositions = [],
  dynamicGeneration = null,
  instanceId = null,
}: PortfolioInputWidgetProps) {
  const { isPublic, sessionToken } = usePublicSession();
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const { formatNumber, parseNumber } = useRegionalFormat();
  const t = useT(T);
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  // The server tells us which snapshots its recalculation touched. Both helpers
  // below translate those hints into query keys, including the scopes that
  // cannot be derived from a portfolioId (QL-315's goal- and instance-keyed
  // asset-class forecast).
  const invalidatePortfolioSnapshots = useInvalidatePortfolioSnapshots();
  const invalidatePublicPortfolioSnapshots = useInvalidatePublicPortfolioSnapshots();

  // A follow-up's schedule type is pinned to its source. The form starts from
  // the locked value even when the stored row disagrees (a legacy seed): the
  // reset effect below records the stored value as "previous", so autosave
  // writes the locked value back and heals the row.
  const lockedScheduleType = followupSource?.lockedScheduleType ?? null;

  const [name, setName] = useState(initialData?.name ?? "");
  const [scheduleType, setScheduleType] = useState<"one_time" | "recurring">(
    lockedScheduleType ?? ((initialData?.scheduleType ?? "one_time") as "one_time" | "recurring"),
  );
  const [oneTimeAmount, setOneTimeAmount] = useState(initialData?.oneTimeAmount?.toString() ?? "");
  const [oneTimeDate, setOneTimeDate] = useState(() => parseInitialDate(initialData?.oneTimeDate));
  const [recurringAmount, setRecurringAmount] = useState(
    initialData?.recurringAmount?.toString() ?? "",
  );
  const [recurringFrequency, setRecurringFrequency] = useState<RecurringFrequency>(
    (initialData?.recurringFrequency ?? RecurringFrequency.MONTHLY) as RecurringFrequency,
  );
  const [recurringStartDate, setRecurringStartDate] = useState(() =>
    parseInitialDate(initialData?.recurringStartDate),
  );
  const [recurringEndDate, setRecurringEndDate] = useState(() =>
    parseInitialDate(initialData?.recurringEndDate),
  );
  const [portfolioType, setPortfolioType] = useState<PortfolioType>(
    (initialData?.portfolioType ?? "model") as PortfolioType,
  );
  // QL-72 — a model-portfolio pick waiting for the delayed save, so it goes out
  // in the same request as the other fields. `undefined` = nothing to send
  // (the server keeps the selection), `null` = back to the matching portfolio.
  const [pendingModelPick, setPendingModelPick] = useState<string | null | undefined>(undefined);
  // QL-282 — how the advisor is entering the recurring amount. Only the
  // per-occurrence amount is ever saved; "total" divides the entered sum by the
  // deposit count and writes the result to `recurringAmount`, so the mode never
  // reaches the server and a reopened portfolio always starts per-occurrence.
  const [amountModeChoice, setAmountModeChoice] = useState<"per_deposit" | "total">("per_deposit");
  const [totalAmount, setTotalAmount] = useState("");

  const prevValuesRef = useRef<{
    portfolioId: string;
    name: string;
    scheduleType: string;
    oneTimeAmount: string;
    oneTimeDate: string;
    recurringAmount: string;
    recurringFrequency: string;
    recurringStartDate: string;
    recurringEndDate: string;
    portfolioType: string;
  } | null>(null);

  // Reset form state when portfolioId changes (switching between portfolio tabs).
  useEffect(() => {
    const next = {
      name: initialData?.name ?? "",
      scheduleType: (initialData?.scheduleType ?? "one_time") as "one_time" | "recurring",
      oneTimeAmount: initialData?.oneTimeAmount?.toString() ?? "",
      oneTimeDate: parseInitialDate(initialData?.oneTimeDate),
      recurringAmount: initialData?.recurringAmount?.toString() ?? "",
      recurringFrequency: (initialData?.recurringFrequency ??
        RecurringFrequency.MONTHLY) as RecurringFrequency,
      recurringStartDate: parseInitialDate(initialData?.recurringStartDate),
      recurringEndDate: parseInitialDate(initialData?.recurringEndDate),
      portfolioType: (initialData?.portfolioType ?? "model") as PortfolioType,
    };
    setName(next.name);
    setScheduleType(lockedScheduleType ?? next.scheduleType);
    setOneTimeAmount(next.oneTimeAmount);
    setOneTimeDate(next.oneTimeDate);
    setRecurringAmount(next.recurringAmount);
    setRecurringFrequency(next.recurringFrequency);
    setRecurringStartDate(next.recurringStartDate);
    setRecurringEndDate(next.recurringEndDate);
    setPortfolioType(next.portfolioType);
    setPendingModelPick(undefined);
    // QL-282 — the amount mode is per-portfolio UI state, not a stored value.
    setAmountModeChoice("per_deposit");
    setTotalAmount("");
    prevValuesRef.current = { portfolioId, ...next };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  // QP-185 — a new generatedAt means the server (re)generated this dynamic
  // composition, typically lazily inside the portfolios GET after a
  // collection-side edit invalidated it. Completion only refetches on
  // mutations, so without this a pure re-view would leave Submit blocked; the
  // portfolio's snapshot queries may also have fetched mid-regeneration.
  useEffect(() => {
    const generatedAt = dynamicGeneration?.generatedAt;
    if (!generatedAt) return;
    if (seenDynamicGenerations.get(portfolioId) === generatedAt) return;
    seenDynamicGenerations.set(portfolioId, generatedAt);
    if (isPublic) {
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
      queryClient.invalidateQueries({
        queryKey: publicPortfolioSnapshotQueryKeys.allForPortfolio(portfolioId),
      });
    } else {
      if (instanceId) invalidateCompletion(instanceId);
      queryClient.invalidateQueries({
        queryKey: portfolioSnapshotQueryKeys.allForPortfolio(portfolioId),
      });
    }
  }, [
    portfolioId,
    dynamicGeneration?.generatedAt,
    isPublic,
    instanceId,
    invalidateCompletion,
    queryClient,
  ]);

  // QL-556 — the placeholders state what an unset month becomes on the orders.
  // New advice: the current month / +10 years. An agreement follow-up keeps the
  // agreement's own dates, so its unset end is open-ended and gets no default.
  const isAgreementFollowup = followupSource?.kind === "savingsPlan";
  const defaultDates = useMemo(() => {
    if (isAgreementFollowup) {
      return { defaultStart: t.agreementStart, defaultEnd: t.openEnded };
    }
    const effectiveStartDate = recurringStartDate
      ? new Date(recurringStartDate)
      : defaultScheduleStartDate();
    const prefix = (s: string) => `${t.defaultPrefix} ${s}`;
    return {
      defaultStart: prefix(
        formatMonthYear(defaultScheduleStartDate(), userLanguage, { monthStyle: "long" }),
      ),
      defaultEnd: prefix(
        formatMonthYear(defaultScheduleEndDate(effectiveStartDate), userLanguage, {
          monthStyle: "long",
        }),
      ),
    };
  }, [isAgreementFollowup, recurringStartDate, userLanguage, t]);

  // QL-282 — the plan's deposit count, and with it the total/per-occurrence
  // split, only exists once the advisor has picked both months. The `Default:`
  // placeholders are not a period: the state is still empty and `countDeposits`
  // returns null, which is what keeps the switch and the helper line hidden.
  const depositCount = useMemo(
    () => countDeposits(recurringStartDate, recurringEndDate, recurringFrequency),
    [recurringStartDate, recurringEndDate, recurringFrequency],
  );
  const canEnterTotal = showDateInputs && depositCount !== null;
  const amountMode = canEnterTotal ? amountModeChoice : "per_deposit";

  const perDepositAmount = recurringAmount === "" ? null : Number.parseFloat(recurringAmount);
  const derivedTotal = totalFromPerDeposit(perDepositAmount, depositCount);
  const frequencyLabels = byFrequency(recurringFrequency, {
    [RecurringFrequency.MONTHLY]: { amount: t.amountPerMonth, per: t.perMonth },
    [RecurringFrequency.QUARTERLY]: { amount: t.amountPerQuarter, per: t.perQuarter },
    [RecurringFrequency.YEARLY]: { amount: t.amountPerYear, per: t.perYear },
  });

  /**
   * QL-282 — re-split after the plan's period changed. In total mode the
   * entered sum is what the advisor committed to, so it is held fixed and the
   * per-occurrence amount follows the new count; in per-occurrence mode the
   * entered amount is the commitment and nothing is re-derived. A period that
   * has become unusable drops back to per-occurrence entry, keeping the last
   * amount it calculated — the total is never left standing as one deposit.
   */
  const resplitForPeriod = (nextStart: string, nextEnd: string, nextFreq: RecurringFrequency) => {
    if (amountMode !== "total") return;
    const nextCount = countDeposits(nextStart, nextEnd, nextFreq);
    if (nextCount === null) {
      setAmountModeChoice("per_deposit");
      return;
    }
    const parsedTotal = totalAmount === "" ? null : Number.parseFloat(totalAmount);
    const nextPerDeposit = perDepositFromTotal(parsedTotal, nextCount);
    if (nextPerDeposit !== null) setRecurringAmount(String(nextPerDeposit));
  };

  const handleAmountModeChange = (next: "per_deposit" | "total") => {
    if (next === amountMode) return;
    // Switching converts the displayed value; the plan itself is untouched.
    // Back to per-occurrence needs no conversion — `recurringAmount` has held
    // the per-occurrence figure all along.
    if (next === "total") {
      setTotalAmount(derivedTotal === null ? "" : String(derivedTotal));
    }
    setAmountModeChoice(next);
  };

  // The total is only an input aid, so once the recurring amount leaves the
  // screen (one-time schedule, custom portfolio) it is dropped. Coming back
  // starts from the saved per-occurrence amount instead of an old total.
  const resetTotalEntry = () => {
    setAmountModeChoice("per_deposit");
    setTotalAmount("");
  };

  const handleTotalAmountChange = (raw: string) => {
    const parsed = parseNumber(raw);
    setTotalAmount(parsed === null ? "" : String(parsed));
    const nextPerDeposit = perDepositFromTotal(parsed, depositCount);
    setRecurringAmount(nextPerDeposit === null ? "" : String(nextPerDeposit));
  };

  const [isSaving, setIsSaving] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  // QP-185 — "dynamic" allowed by the component config but switched off on the
  // collection: the option stays visible, disabled, with an annotation.
  const dynamicGated = allowedPortfolioTypes.includes("dynamic") && !dynamicPortfoliosEnabled;
  // QL-664 — the same gating for model AND dynamic under a mandate template.
  // QL-730 A1 — custom is no longer added here when the component config
  // leaves it out: the flow must allow it itself (the activation guard
  // requires it on a collection with mandates on), and the server refuses
  // the create otherwise. A legacy flow that leaves it out shows the note.
  const mandateGated = templateRestricted;
  const typeOptions: PortfolioType[] = allowedPortfolioTypes;
  const customMissingUnderMandate = mandateGated && !typeOptions.includes("custom");

  // Auto-correct portfolio type if it doesn't match allowed types. A gated
  // "dynamic" is never picked as the replacement (no silent fallback — with no
  // selectable type left, the value is simply left alone). A portfolio that
  // already IS model / dynamic on a restricted goal keeps its value (QL-664):
  // the completion marker asks the advisor to switch it, nothing moves silently.
  useEffect(() => {
    if (disabled) return;
    if (!typeOptions.includes(portfolioType)) {
      const selectable = typeOptions.filter(
        (type) =>
          (type !== "dynamic" || dynamicPortfoliosEnabled) && (!mandateGated || type === "custom"),
      );
      if (selectable.length > 0) setPortfolioType(selectable[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedPortfolioTypes, disabled, dynamicPortfoliosEnabled, mandateGated]);

  const showAlert = (
    alertTitle: string,
    alertDescription: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title: alertTitle, description: alertDescription, variant });
    setAlertOpen(true);
  };

  const handleSave = async () => {
    if (disabled) return;
    setIsSaving(true);
    try {
      const oneTimeAmountNum = parseFloat(oneTimeAmount);
      const recurringAmountNum = parseFloat(recurringAmount);
      const toFirstOfMonth = (yearMonth: string) => (yearMonth ? `${yearMonth}-01` : null);
      const sentModelPick = portfolioType === "model" ? pendingModelPick : undefined;

      const formData = {
        name,
        scheduleType,
        oneTimeAmount:
          scheduleType === "one_time"
            ? Number.isNaN(oneTimeAmountNum)
              ? null
              : oneTimeAmountNum
            : null,
        oneTimeDate: scheduleType === "one_time" ? toFirstOfMonth(oneTimeDate) : null,
        recurringAmount:
          scheduleType === "recurring"
            ? Number.isNaN(recurringAmountNum)
              ? null
              : recurringAmountNum
            : null,
        recurringFrequency: scheduleType === "recurring" ? recurringFrequency : null,
        recurringStartDate:
          scheduleType === "recurring" ? toFirstOfMonth(recurringStartDate) : null,
        recurringEndDate: scheduleType === "recurring" ? toFirstOfMonth(recurringEndDate) : null,
        portfolioType,
        // QL-72 — the pick travels with the type, so a pick right after
        // switching to model is accepted. Sent only when changed.
        ...(sentModelPick !== undefined && { pickedPortfolioId: sentModelPick }),
      };

      let result: SaveResult | undefined;
      let portfolioRefetch: Promise<void>;

      if (isPublic) {
        result = await publicPortfoliosApi.updatePublicPortfolio(
          portfolioId,
          formData,
          sessionToken,
        );
        portfolioRefetch = invalidateAfterPublicSave();
      } else {
        result = await portfoliosApi.updatePortfolio(portfolioId, formData);
        portfolioRefetch = invalidateAfterAdvisorSave(result);
      }

      await finishSave(result);

      if (sentModelPick !== undefined) {
        // Keep showing the pick until the picker's query has the saved one,
        // and keep a newer pick made while this save was in flight.
        await portfolioRefetch;
        setPendingModelPick((p) => (p === sentModelPick ? undefined : p));
      }
    } catch (_err) {
      // The picker falls back to the saved selection.
      setPendingModelPick(undefined);
      showAlert(t.error, t.failedToSave, "destructive");
    } finally {
      setIsSaving(false);
    }
  };

  // Public: refresh the public portfolio queries + completion (the session encodes the instance).
  // Returns the refetch of this portfolio's queries (the model picker's included).
  const invalidateAfterPublicSave = () => {
    const portfolioRefetch = queryClient.invalidateQueries({
      queryKey: publicPortfolioKeys.portfolio(portfolioId),
    });
    invalidatePublicGoalPortfolios(queryClient, goalId);
    // QL-362 — a schedule change moves this portfolio's buys in or out of the
    // goal's shared accounts, so sibling portfolios' violations are stale too.
    invalidatePublicPortfolioFamily(queryClient);
    queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    // QPBI-321 — conditional-display rules read portfolioType/scheduleType from
    // the goals query, so a schedule/type change must refresh it too.
    queryClient.invalidateQueries({ queryKey: publicGoalQueryKeys.goals() });
    return portfolioRefetch;
  };

  // Advisor: refresh this portfolio + its goal's portfolio list, and completion.
  const invalidateAfterAdvisorSave = (result: SaveResult | undefined) => {
    const portfolioRefetch = queryClient.invalidateQueries({
      queryKey: portfolioQueryKeys.portfolio(portfolioId),
    });
    invalidateGoalPortfolios(queryClient, goalId);
    // QL-362 — see the public branch: siblings feeding the same account.
    invalidatePortfolioFamily(queryClient);
    if (result?.instanceId) {
      invalidateCompletion(result.instanceId);
      // QPBI-321 — conditional-display rules read portfolioType/scheduleType
      // from the instance goals query, so a schedule/type change must refresh it.
      queryClient.invalidateQueries({ queryKey: goalQueryKeys.goals(result.instanceId) });
    }
    return portfolioRefetch;
  };

  const finishSave = async (result: SaveResult | undefined) => {
    // Saving an amount or a schedule re-runs the portfolio's whole analytics
    // family server-side. Without this the widget refreshed only the
    // portfolio row and left every chart on the page showing the previous
    // numbers until the tab was remounted.
    if (result?.refreshHints) {
      if (isPublic) {
        invalidatePublicPortfolioSnapshots(portfolioId, result.refreshHints);
      } else {
        invalidatePortfolioSnapshots(portfolioId, result.refreshHints);
      }
    }

    if (onSaveComplete) {
      await onSaveComplete(result?.refreshHints);
    }
  };

  // Auto-save on change (debounced).
  useEffect(() => {
    const current = {
      portfolioId,
      name,
      scheduleType,
      oneTimeAmount,
      oneTimeDate,
      recurringAmount,
      recurringFrequency,
      recurringStartDate,
      recurringEndDate,
      portfolioType,
    };
    if (!prevValuesRef.current || prevValuesRef.current.portfolioId !== portfolioId) {
      prevValuesRef.current = current;
      return;
    }
    const prev = prevValuesRef.current;
    const hasChanges =
      prev.name !== name ||
      prev.scheduleType !== scheduleType ||
      prev.oneTimeAmount !== oneTimeAmount ||
      prev.oneTimeDate !== oneTimeDate ||
      prev.recurringAmount !== recurringAmount ||
      prev.recurringFrequency !== recurringFrequency ||
      prev.recurringStartDate !== recurringStartDate ||
      prev.recurringEndDate !== recurringEndDate ||
      prev.portfolioType !== portfolioType ||
      // A pending model pick is a change until the save clears it.
      pendingModelPick !== undefined;
    if (!hasChanges) return;
    prevValuesRef.current = current;
    const timeoutId = setTimeout(() => {
      handleSave();
    }, 1000);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    portfolioId,
    name,
    scheduleType,
    oneTimeAmount,
    oneTimeDate,
    recurringAmount,
    recurringFrequency,
    recurringStartDate,
    recurringEndDate,
    portfolioType,
    pendingModelPick,
  ]);

  // QL-198 — the goal's platform state (shared query with goal_input, both
  // surfaces) so the model-resolution banner can name the platform. The query
  // is cached under the same key the goal_input widget uses.
  const { platformState } = useGoalPlatformState(goalId);

  // QL-198 — specific banner for an unresolvable model portfolio: the goal has
  // a risk score (and possibly a platform), but no active model portfolio in
  // the collection matches (QP-226 — explicit platform membership, no untagged
  // fallback). Mirrors the dynamic generation-error banner.
  const modelResolutionMessage = (() => {
    if (portfolioType !== "model") return null;
    const score = goalRiskScore != null ? String(goalRiskScore) : "";
    if (missingReason === "no_model_portfolio_for_score_on_platform") {
      const selected = platformState?.eligible.find((p) => p.id === platformState?.selectedId);
      const label = selected ? localize(selected.labelI18n) : "";
      return label
        ? t.noModelPortfolioForScoreOnPlatform(score, label)
        : t.noModelPortfolioForScore(score);
    }
    if (missingReason === "no_model_portfolio_for_score") {
      return t.noModelPortfolioForScore(score);
    }
    // QL-664 — a model / dynamic portfolio on a template-governed goal.
    // PR 701 review — on a legacy flow without custom, "switch to custom"
    // offers what is not there: the note below says what to do instead.
    if (missingReason === "portfolio_type_restricted_by_mandate") {
      return customMissingUnderMandate ? null : t.switchToCustomForMandate;
    }
    return null;
  })();

  // QL-362 — shown live (not gated on `hasValidationError`) so the advisor can
  // react while entering the amount rather than at submit.
  const accountMinimumLines =
    portfolioType === "custom"
      ? []
      : formatAccountMinimumLines(
          accountMinimumViolations,
          t.accountMinimumItem,
          localize,
          formatNumber,
        );

  const getContainerClass = () => {
    if (hasValidationError) {
      return "border border-destructive rounded-lg p-6 space-y-6";
    }
    switch (style) {
      case "border":
        return "border rounded-lg p-6 space-y-6";
      case "box":
        return "rounded-lg p-6 space-y-6 bg-muted/50";
      default:
        return "space-y-6";
    }
  };

  return (
    <>
      <div className={getContainerClass()}>
        <FlowComponentHeader title={title} description={description} />

        {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}

        {/* QL-198 — unresolvable model portfolio: specific reason banner */}
        {modelResolutionMessage && (
          <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
            {modelResolutionMessage}
          </div>
        )}

        {/* QL-398 — an account follow-up's per-transaction entries are listed by
            the builder (one line per top-up / switch pair); only the other
            kinds keep this list. */}
        {minBuyViolations.some((v) => !v.followup) && (
          <div className="text-sm text-destructive space-y-1">
            <p>{t.minBuyHeading}</p>
            <ul className="list-disc pl-5">
              {minBuyViolations
                .filter((v) => !v.followup)
                .map((v) => (
                  <li key={v.productId}>
                    {t.minBuyItem({
                      product: localize(v.productNameI18n),
                      min: formatNumber(v.minBuyAmount, { maximumFractionDigits: 2 }),
                      required: formatNumber(
                        v.requiredPortfolioAmount != null
                          ? Math.ceil(v.requiredPortfolioAmount)
                          : v.minBuyAmount,
                        { maximumFractionDigits: 2 },
                      ),
                    })}
                  </li>
                ))}
            </ul>
          </div>
        )}

        {/* QL-362 — one line per breaching account, live from the portfolio payload. */}
        {missingReason === "account_below_minimum" && (
          <p className="text-sm text-destructive">{t.accountMinimumBlockedMessage}</p>
        )}
        {/* QL-556 — the server refuses an end month before the (effective) start month. */}
        {missingReason === "recurring_end_before_start" && (
          <p className="text-sm text-destructive">{t.endBeforeStartMessage}</p>
        )}
        {accountMinimumLines.map((line) => (
          <p key={line.key} className="text-sm text-destructive">
            {line.text}
          </p>
        ))}

        {isSaving && <div className="text-sm text-muted-foreground">{t.saving}</div>}

        {/* Portfolio Name */}
        {showPortfolioName && (
          <div className="space-y-2">
            <Label htmlFor="portfolioName">
              {t.portfolioName} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="portfolioName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.portfolioNamePlaceholder}
              disabled={disabled}
              required
            />
          </div>
        )}

        {/* Portfolio Type — a gated dynamic keeps the selector visible even for a
            dynamic-only config, so the disabled option (not a blank page) explains
            why the type is unavailable. */}
        {showPortfolioType && (typeOptions.length > 1 || dynamicGated || mandateGated) && (
          <div className="space-y-2">
            <Label htmlFor="portfolioType">{t.portfolioType}</Label>
            <Select
              value={portfolioType}
              onValueChange={(value) => {
                if (value === "custom") resetTotalEntry();
                // A pick only exists on a model portfolio.
                if (value !== "model") setPendingModelPick(undefined);
                setPortfolioType(value as PortfolioType);
              }}
              disabled={disabled}
            >
              <SelectTrigger id="portfolioType">
                <SelectValue placeholder={t.selectPortfolioType} />
              </SelectTrigger>
              <SelectContent>
                {typeOptions.includes("model") && (
                  <SelectItem value="model" disabled={mandateGated}>
                    <span>{t.modelPortfolio}</span>
                    {mandateGated && (
                      <span className="text-muted-foreground"> — {t.typeRestrictedByMandate}</span>
                    )}
                  </SelectItem>
                )}
                {typeOptions.includes("custom") && (
                  <SelectItem value="custom">{t.customPortfolio}</SelectItem>
                )}
                {typeOptions.includes("dynamic") && (
                  <SelectItem value="dynamic" disabled={dynamicGated || mandateGated}>
                    <span>{t.dynamicPortfolio}</span>
                    {(dynamicGated || mandateGated) && (
                      <span className="text-muted-foreground">
                        {" "}
                        —{" "}
                        {mandateGated ? t.typeRestrictedByMandate : t.dynamicDisabledForCollection}
                      </span>
                    )}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {customMissingUnderMandate && (
              // PR 701 review — a client on the public flow cannot ask an
              // administrator; the advisor finishes the portfolio.
              <p className="text-sm text-destructive">
                {isPublic ? t.customMissingUnderMandatePublic : t.customMissingUnderMandate}
              </p>
            )}
          </div>
        )}

        {/* QL-72 — model portfolio from another risk score (advisor and client).
            With the setting off, a kept pick still shows so it can be undone. */}
        {portfolioType === "model" && (modelDeviationEnabled || pickedPortfolioId != null) && (
          <ModelPortfolioSelect
            portfolioId={portfolioId}
            goalRiskScore={goalRiskScore ?? null}
            platformId={platformState?.selectedId ?? null}
            disabled={disabled || isSaving}
            onChange={(id) => {
              if (!disabled) setPendingModelPick(id);
            }}
            pendingValue={pendingModelPick}
          />
        )}

        {/* Schedule Type */}
        <div className="space-y-2">
          <Label htmlFor="scheduleType">
            {t.scheduleType} <span className="text-destructive">*</span>
          </Label>
          <Select
            value={scheduleType}
            onValueChange={(value) => {
              if (value !== "recurring") resetTotalEntry();
              setScheduleType(value as "one_time" | "recurring");
            }}
            disabled={disabled || !!lockedScheduleType}
          >
            <SelectTrigger id="scheduleType">
              <SelectValue placeholder={t.selectScheduleType} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="one_time">{t.oneTimeLumpSum}</SelectItem>
              <SelectItem value="recurring">{t.recurringContributions}</SelectItem>
            </SelectContent>
          </Select>
          {followupSource && (
            <Alert variant="info">
              <Info />
              <AlertDescription>
                {(followupSource.kind === "account"
                  ? t.scheduleLockedAccount
                  : t.scheduleLockedSavingsPlan)(formatFollowupSourceLabel(followupSource))}
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* One-time Schedule Fields */}
        {scheduleType === "one_time" && (
          <>
            {portfolioType !== "custom" && (
              <div className="space-y-2">
                <Label htmlFor="oneTimeAmount">
                  {t.amount} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="oneTimeAmount"
                  type="text"
                  inputMode="decimal"
                  value={formatNumber(
                    oneTimeAmount === "" ? undefined : Number.parseFloat(oneTimeAmount),
                    { maximumFractionDigits: 2 },
                  )}
                  onChange={(e) => {
                    const parsed = parseNumber(e.target.value);
                    setOneTimeAmount(parsed === null ? "" : String(parsed));
                  }}
                  placeholder={formatNumber(50000, { maximumFractionDigits: 2 })}
                  disabled={disabled}
                  required
                />
              </div>
            )}

            {showDateInputs && (
              <div className="space-y-2">
                <Label htmlFor="oneTimeDate">{t.monthOptional}</Label>
                <MonthYearPicker
                  value={oneTimeDate || null}
                  onChange={(value) => setOneTimeDate(value ?? "")}
                  placeholder={defaultDates.defaultStart}
                  disabled={disabled}
                />
              </div>
            )}
          </>
        )}

        {/* Recurring Schedule Fields */}
        {scheduleType === "recurring" && (
          <>
            {portfolioType !== "custom" && (
              <div className="space-y-4">
                {/* QL-282 — a view switch over one plan, not a saved value: both
                    sides describe the same deposits, so it is a ToggleGroup. */}
                {canEnterTotal && (
                  <ToggleGroup
                    type="single"
                    value={amountMode}
                    onValueChange={(next) => {
                      if (next) handleAmountModeChange(next as "per_deposit" | "total");
                    }}
                    disabled={disabled}
                  >
                    <ToggleGroupItem value="per_deposit">{frequencyLabels.amount}</ToggleGroupItem>
                    <ToggleGroupItem value="total">{t.totalAmount}</ToggleGroupItem>
                  </ToggleGroup>
                )}

                {amountMode === "total" ? (
                  <AmountField
                    id="totalAmount"
                    label={t.totalAmount}
                    value={totalAmount}
                    onChange={handleTotalAmountChange}
                    placeholder={24000}
                    disabled={disabled}
                  >
                    {/* The total is already in the input; what the advisor cannot
                        see is what it becomes per deposit. */}
                    <p className="text-sm text-muted-foreground">
                      {formatNumber(perDepositAmount ?? undefined, { maximumFractionDigits: 2 })}{" "}
                      {frequencyLabels.per} · {t.depositsLabel} {depositCount}
                    </p>
                  </AmountField>
                ) : (
                  <AmountField
                    id="recurringAmount"
                    label={frequencyLabels.amount}
                    value={recurringAmount}
                    onChange={(raw) => {
                      const parsed = parseNumber(raw);
                      setRecurringAmount(parsed === null ? "" : String(parsed));
                    }}
                    placeholder={2000}
                    disabled={disabled}
                  >
                    {canEnterTotal && (
                      <p className="text-sm text-muted-foreground">
                        {t.totalPrefix}{" "}
                        {formatNumber(derivedTotal ?? undefined, { maximumFractionDigits: 2 })} ·{" "}
                        {t.depositsLabel} {depositCount}
                      </p>
                    )}
                  </AmountField>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="recurringFrequency">
                {t.frequency} <span className="text-destructive">*</span>
              </Label>
              <Select
                value={recurringFrequency}
                onValueChange={(value) => {
                  const next = value as RecurringFrequency;
                  setRecurringFrequency(next);
                  resplitForPeriod(recurringStartDate, recurringEndDate, next);
                }}
                disabled={disabled}
              >
                <SelectTrigger id="recurringFrequency">
                  <SelectValue placeholder={t.selectFrequency} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">{t.monthly}</SelectItem>
                  <SelectItem value="quarterly">{t.quarterly}</SelectItem>
                  <SelectItem value="yearly">{t.yearly}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {showDateInputs && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="recurringStartDate">{t.startMonthOptional}</Label>
                  <MonthYearPicker
                    value={recurringStartDate || null}
                    onChange={(value) => {
                      const next = value ?? "";
                      setRecurringStartDate(next);
                      resplitForPeriod(next, recurringEndDate, recurringFrequency);
                    }}
                    placeholder={defaultDates.defaultStart}
                    disabled={disabled}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recurringEndDate">{t.endMonthOptional}</Label>
                  <MonthYearPicker
                    value={recurringEndDate || null}
                    onChange={(value) => {
                      const next = value ?? "";
                      setRecurringEndDate(next);
                      resplitForPeriod(recurringStartDate, next, recurringFrequency);
                    }}
                    placeholder={defaultDates.defaultEnd}
                    disabled={disabled}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* QP-185 — read-only generated composition / generation-error banner */}
        {portfolioType === "dynamic" && (
          <DynamicCompositionView positions={generatedPositions} generation={dynamicGeneration} />
        )}
      </div>

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}

/**
 * One required amount input. The total and the per-occurrence entry share it so
 * the two modes cannot drift apart; `children` is the helper line underneath.
 */
function AmountField({
  id,
  label,
  value,
  onChange,
  placeholder,
  disabled,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (raw: string) => void;
  placeholder: number;
  disabled?: boolean;
  children?: ReactNode;
}) {
  const { formatNumber } = useRegionalFormat();
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} <span className="text-destructive">*</span>
      </Label>
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        value={formatNumber(value === "" ? undefined : Number.parseFloat(value), {
          maximumFractionDigits: 2,
        })}
        onChange={(e) => onChange(e.target.value)}
        placeholder={formatNumber(placeholder, { maximumFractionDigits: 2 })}
        disabled={disabled}
        required
      />
      {children}
    </div>
  );
}
